import {
  DRAFT_FIELDS,
  centsToDollarsInput,
  type DraftField,
  type LotDescriptivePatch,
  type LotDraft,
  type PriceKey,
} from '@auctisync/api-client';

/**
 * The two rules that keep the office's lot FORM honest about the AI draft.
 *
 * The lot page edits a whole lot in one form and saves every field at once.
 * Anything that changes the lot on the server while the form is open is
 * therefore overwritten by the next Save unless the form learns about it.
 * Two things do: accepting the AI's prices (from this page), and the server
 * filling blank fields when a draft arrives (from nowhere the page can see).
 */

/**
 * Copy accepted prices into the form. The form's price keys ARE the shared
 * field keys (`msrp`, `reserve`, …), so this is a lookup, not a mapping.
 */
export function withAcceptedPrices<F extends object>(
  form: F,
  applied: Partial<Record<PriceKey, number>>,
): F {
  const next = { ...form } as Record<string, unknown>;
  for (const field of DRAFT_FIELDS) {
    if (field.group !== 'pricing') continue;
    const cents = applied[field.draftKey as PriceKey];
    if (cents === undefined) continue;
    next[field.key] = centsToDollarsInput(cents);
  }
  return next as F;
}

/**
 * Did the server fill blank fields AFTER the form was loaded? The fill runs in
 * the same write that marks the draft ready, so `completed_at` is when the
 * lot changed underneath the form.
 */
export function filledAfterPageLoad(draft: LotDraft | null, loadedAt: string): boolean {
  return (
    draft !== null &&
    draft.autofilled_fields.length > 0 &&
    draft.completed_at !== null &&
    Date.parse(draft.completed_at) > Date.parse(loadedAt)
  );
}

/** The descriptive part of the lot form — what a draft suggestion can touch. */
export interface DescriptiveForm {
  title: string;
  description: string;
  condition: string;
  category_id: string;
  mpn: string;
  upc: string;
  custom_fields: Record<string, unknown>;
}

/** Is this field blank in the open form — i.e. would a Save write nothing there? */
export function formFieldBlank(form: DescriptiveForm, field: DraftField): boolean {
  const raw =
    'customField' in field.target
      ? form.custom_fields[field.target.customField]
      : field.target.column === 'category_id'
        ? form.category_id
        : (form as unknown as Record<string, unknown>)[field.target.column];
  if (typeof raw === 'string') return raw.trim() === '';
  // A spec can hold a number (a year); anything else present counts as filled.
  return raw === undefined || raw === null;
}

/**
 * Put a taken suggestion into the FORM, not the database. The office is
 * mid-edit; writing behind the form would be undone by its next Save, which
 * writes every field at once. The person sees the value land and saves it.
 */
export function withSuggestion<F extends DescriptiveForm>(form: F, patch: LotDescriptivePatch): F {
  const next = { ...form };
  for (const k of ['title', 'description', 'condition', 'mpn', 'upc'] as const) {
    const v = patch[k];
    if (typeof v === 'string') next[k] = v;
  }
  if (typeof patch.category_id === 'string') next.category_id = patch.category_id;
  if (patch.specs) {
    const specs = { ...next.custom_fields };
    for (const [k, v] of Object.entries(patch.specs)) if (v !== null) specs[k] = v;
    next.custom_fields = specs;
  }
  return next;
}
